import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { finalize, take } from 'rxjs/operators';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { PortalNavbarComponent } from './portal-navbar.component';
import { TestPaymentService } from '../services/test-payment.service';

@Component({
  selector: 'app-payment-services-helcim-connectivity-page',
  standalone: true,
  imports: [CommonModule, RouterModule, PortalNavbarComponent, HeroUserPillComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <div class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/payment-services" class="home-btn">Payment Services</a>
          <div class="hero-top-row">
            <div class="hero-icon hero-icon--emerald">
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                <line x1="2" y1="10" x2="22" y2="10"></line>
              </svg>
            </div>
            <app-hero-user-pill></app-hero-user-pill>
          </div>
          <h1 class="page-title">Payment Services > Helcim Connectivity</h1>
          <p class="page-sub">Validate Helcim API connectivity through the secured backend flow.</p>
        </div>
      </div>

      <section class="page-body">
        <div class="page-body-inner">
          <div class="card">
            <div class="card-hd">
              <span class="card-title">Connection Test</span>
              <button type="button" class="test-btn" (click)="testConnection()" [disabled]="isTestingConnection">
                {{ isTestingConnection ? 'Testing...' : 'Test Connection' }}
              </button>
            </div>

            @if (connectionMessage) {
              <div class="result" [class.result--ok]="connectionMessage.includes('✓')" [class.result--error]="connectionMessage.includes('✗')">
                {{ connectionMessage }}
              </div>
            }
          </div>
        </div>
      </section>
    </div>
  `,
  styles: [`.page{min-height:100vh}.page-hero{border-bottom:1px solid var(--color-border);padding:2rem 1.5rem;background:linear-gradient(155deg,var(--color-bg-start) 0%,var(--color-bg-end) 100%)}.page-hero-inner,.page-body-inner{max-width:1080px;margin:0 auto}.home-btn{color:var(--color-muted);text-decoration:none}.hero-top-row{align-items:center;display:flex;flex-wrap:wrap;gap:.85rem;margin-top:.75rem}.hero-icon{align-items:center;border-radius:0.9rem;display:flex;height:56px;justify-content:center;width:56px}.hero-icon--emerald{background:color-mix(in srgb,#10b981 18%,transparent);border:1px solid color-mix(in srgb,#10b981 35%,transparent);color:#34d399}.page-title{margin:.8rem 0 0}.page-sub{margin:.4rem 0 0;color:var(--color-muted)}.page-body{padding:1.5rem}.card{border:1px solid var(--color-border);border-radius:.9rem;background:color-mix(in srgb,var(--color-panel) 80%,transparent)}.card-hd{border-bottom:1px solid var(--color-border);padding:.8rem 1rem;display:flex;align-items:center;justify-content:space-between}.card-title{font-weight:600}.test-btn{border:1px solid var(--color-border);background:transparent;color:var(--color-text);border-radius:.45rem;padding:.4rem .7rem;cursor:pointer;font-size:.85rem}.test-btn:hover{background:color-mix(in srgb,var(--color-accent) 10%,transparent)}.test-btn:disabled{opacity:.6;cursor:not-allowed}.result{margin:1rem;padding:.75rem;border-radius:.55rem}.result--ok{border:1px solid color-mix(in srgb,#10b981 40%,transparent);background:color-mix(in srgb,#10b981 10%,transparent)}.result--error{border:1px solid color-mix(in srgb,#ef4444 40%,transparent);background:color-mix(in srgb,#ef4444 10%,transparent)}`]
})
export class PaymentServicesHelcimConnectivityPageComponent {
  private readonly testPaymentService = inject(TestPaymentService);
  private readonly cdr = inject(ChangeDetectorRef);

  isTestingConnection = false;
  connectionMessage = '';

  testConnection(): void {
    if (this.isTestingConnection) {
      return;
    }

    this.connectionMessage = '';
    this.isTestingConnection = true;

    this.testPaymentService.testConnection()
      .pipe(
        take(1),
        finalize(() => {
          this.isTestingConnection = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (response) => {
          const connected = response?.connected === true;
          this.connectionMessage = connected ? '✓ Connection successful!' : '✗ Connection failed';
          this.cdr.detectChanges();
        },
        error: (err) => {
          const message = err?.error?.title || err?.error?.message || 'Connection test failed.';
          this.connectionMessage = `✗ ${message}`;
          this.cdr.detectChanges();
        }
      });
  }
}
